import React, { useEffect, useState } from 'react';
import CookieConsent from 'react-cookie-consent';
import { Link } from 'react-router-dom';
import './CookieConsent.css';

const preferenceCookieName = 'thriveCookiePreferences';
const consentCookieName = 'thriveCookieConsent';
const defaultPreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
};

const getCookieValue = (name) =>
  document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));

const getPreferences = () => {
  const cookieValue = getCookieValue(preferenceCookieName);
  if (!cookieValue) return defaultPreferences;

  try {
    return JSON.parse(decodeURIComponent(cookieValue.split('=')[1]));
  } catch {
    return defaultPreferences;
  }
};

const setPreferences = (preferences) => {
  const value = encodeURIComponent(JSON.stringify(preferences));
  document.cookie = `${preferenceCookieName}=${value}; path=/; max-age=${60 * 60 * 24 * 150}; SameSite=Lax`;
};

const CookieConsentBanner = () => {
  const [preferences, setPreferencesState] = useState(defaultPreferences);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    setPreferencesState(getPreferences());
  }, []);

  const updatePreference = (key, value) => {
    setPreferencesState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSavePreferences = () => {
    setPreferences(preferences);
    window.dispatchEvent(new Event('cookieConsentChange'));
    setShowPreferences(false);
  };

  const handleAccept = () => {
    const allPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
    };

    setPreferences(allPreferences);
    setPreferencesState(allPreferences);
    window.dispatchEvent(new Event('cookieConsentChange'));
    console.log('Cookies accepted');
  };

  const handleDecline = () => {
    const declinedPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
    };

    setPreferences(declinedPreferences);
    setPreferencesState(declinedPreferences);
    window.dispatchEvent(new Event('cookieConsentChange'));
    console.log('Cookies declined');
  };

  const handleRejectNonEssential = () => {
    const declinedPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
    };

    setPreferencesState(declinedPreferences);
    setPreferences(declinedPreferences);
    window.dispatchEvent(new Event('cookieConsentChange'));
    setShowPreferences(false);
  };

  return (
    <>
      <CookieConsent
        location="bottom"
        cookieName={consentCookieName}
        expires={150}
        enableDeclineButton
        buttonText="Accept"
        declineButtonText="Decline"
        containerClasses="cookie-banner"
        contentClasses="cookie-banner-content"
        buttonWrapperClasses="cookie-banner-actions"
        buttonClasses="cookie-btn accept-btn"
        declineButtonClasses="cookie-btn decline-btn"
        disableStyles={true}
        onAccept={handleAccept}
        onDecline={handleDecline}
      >
        <div className="cookie-message">
          <p>
            This website uses cookies to improve your browsing experience,
            personalize content, and analyze traffic. By continuing to use this
            website, you agree to our use of cookies.
          </p>

          <div className="cookie-banner-links">
            <Link to="/cookie-policy" className="cookie-link">
              Learn More
            </Link>
            <button
              type="button"
              className="cookie-manage-btn"
              onClick={() => setShowPreferences(true)}
            >
              Manage preferences
            </button>
          </div>
        </div>
      </CookieConsent>

      {showPreferences && (
        <div className="cookie-preferences-modal">
          <div className="cookie-preferences-panel">
            <div className="cookie-preferences-header">
              <h3>Cookie preferences</h3>
              <button
                type="button"
                className="cookie-preferences-close"
                onClick={() => setShowPreferences(false)}
                aria-label="Close preferences"
              >
                ×
              </button>
            </div>
            <p className="cookie-preferences-description">
              Choose which cookies you want to allow. Necessary cookies are
              always enabled.
            </p>

            <div className="cookie-preferences-list">
              <div className="cookie-preference-item">
                <div>
                  <strong>Necessary cookies</strong>
                  <p>Required for the site to work correctly.</p>
                </div>
                <div className="cookie-preference-toggle">
                  <span className="cookie-preference-badge">Required</span>
                </div>
              </div>

              <div className="cookie-preference-item">
                <div>
                  <strong>Analytics cookies</strong>
                  <p>Help us understand visitor behavior and improve the site.</p>
                </div>
                <label className="cookie-switch">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => updatePreference('analytics', e.target.checked)}
                  />
                  <span className="cookie-switch-slider" />
                </label>
              </div>

              <div className="cookie-preference-item">
                <div>
                  <strong>Marketing cookies</strong>
                  <p>Used to personalize ads and promotions.</p>
                </div>
                <label className="cookie-switch">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => updatePreference('marketing', e.target.checked)}
                  />
                  <span className="cookie-switch-slider" />
                </label>
              </div>
            </div>

            <div className="cookie-preferences-actions">
              <button
                type="button"
                className="cookie-btn decline-btn"
                onClick={handleRejectNonEssential}
              >
                Reject non-essential
              </button>
              <button
                type="button"
                className="cookie-btn accept-btn"
                onClick={handleSavePreferences}
              >
                Save preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsentBanner;
